import React, { useState } from 'react';
import { Settings, Trash2, CheckCircle } from 'lucide-react';

const BulkActionsPanel = ({ selectedCount, communes, onBulkAction }) => {
  const [showActions, setShowActions] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({});

  const handleAction = async () => {
    if (!actionType) return;

    const confirmed = window.confirm(`Confirmer cette action sur ${selectedCount} propriétés ?`);
    if (!confirmed) return;

    await onBulkAction?.(actionType, actionData);
    setShowActions(false);
    setActionType('');
    setActionData({});
  };

  const statuses = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <CheckCircle className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-900">
            {selectedCount} propriétés sélectionnées
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowActions(!showActions)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Actions groupées
          </button>
        </div>
      </div>

      {showActions && (
        <div className="mt-4 border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Change */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Changer le statut
              </label>
              <div className="flex space-x-2">
                <select
                  value={actionType === 'status' ? actionData?.status || '' : ''}
                  onChange={(e) => {
                    if (e?.target?.value) {
                      setActionType('status');
                      setActionData({ status: e?.target?.value });
                    } else {
                      setActionType('');
                      setActionData({});
                    }
                  }}
                  className="text-sm px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  {statuses?.map(status => (
                    <option key={status?.value} value={status?.value}>{status?.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Commune Change */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Changer la commune
              </label>
              <div className="flex space-x-2">
                <select
                  value={actionType === 'commune' ? actionData?.communeId || '' : ''}
                  onChange={(e) => {
                    if (e?.target?.value) {
                      setActionType('commune');
                      setActionData({ communeId: e?.target?.value });
                    } else {
                      setActionType('');
                      setActionData({});
                    }
                  }}
                  className="text-sm px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  {communes?.map(commune => (
                    <option key={commune?.id} value={commune?.id}>
                      {commune?.name} - {commune?.district}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Delete Action */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Supprimer
              </label>
              <button
                onClick={() => {
                  setActionType('delete');
                  setActionData({});
                }}
                className={`text-sm px-3 py-1 rounded-md transition-colors ${
                  actionType === 'delete' 
                    ? 'bg-red-100 text-red-800 border border-red-200' :'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600'
                }`}
              >
                <Trash2 className="h-3 w-3 mr-1 inline" />
                Supprimer tout
              </button>
            </div>

            {/* Execute Action */}
            <div className="flex items-end">
              <button
                onClick={handleAction}
                disabled={!actionType}
                className="w-full px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Exécuter
              </button>
            </div>
          </div>

          {/* Action Preview */}
          {actionType && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                {actionType === 'status' && `Modifier le statut vers "${statuses?.find(s => s?.value === actionData?.status)?.label}" pour ${selectedCount} propriétés`}
                {actionType === 'commune' && `Déplacer ${selectedCount} propriétés vers "${communes?.find(c => c?.id === actionData?.communeId)?.name}"`}
                {actionType === 'delete' && `Supprimer définitivement ${selectedCount} propriétés`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkActionsPanel;